interface HoverCallbacks {
  onMove: (event: PointerEvent) => void;
  onLeave: () => void;

  /** Coalesces moves into an animation frame unless immediate synchronization is needed. */
  deferMoves?: boolean;
}

/** Tracks pointer hover and clears it across surface and window boundaries. */
export function trackPointerHover(
  element: Element,
  { onMove, onLeave, deferMoves = true }: HoverCallbacks
) {
  const document = element.ownerDocument;
  const view = document.defaultView;
  if (!view) return () => {};

  let latest: PointerEvent | null = null;
  let frame: number | null = null;
  let inside = true;
  const cancelFrame = () => {
    if (frame != null) view.cancelAnimationFrame(frame);
    frame = null;
    latest = null;
  };
  const leave = () => {
    cancelFrame();
    inside = false;
    onLeave();
  };
  const contains = (target: EventTarget | null) =>
    target != null && element.contains(target as Node);
  const settle = () => {
    frame = null;
    const event = latest;
    if (!event) return;
    const rect = element.getBoundingClientRect();
    if (
      event.clientX < rect.left ||
      event.clientX >= rect.right ||
      event.clientY < rect.top ||
      event.clientY >= rect.bottom ||
      !contains(document.elementFromPoint(event.clientX, event.clientY))
    ) {
      leave();
      return;
    }
    onMove(event);
  };
  const move = (event: PointerEvent) => {
    if (!contains(event.target)) {
      if (inside) leave();
      return;
    }
    inside = true;
    latest = event;
    if (deferMoves) {
      frame ??= view.requestAnimationFrame(settle);
    } else {
      settle();
    }
  };
  const out = (event: PointerEvent) => {
    if (event.relatedTarget == null) leave();
  };
  const up = (event: PointerEvent) => {
    if (event.pointerType === 'touch') leave();
  };
  const blur = (event: FocusEvent) => {
    if (!contains(event.relatedTarget)) leave();
  };
  const visibility = () => {
    if (document.visibilityState !== 'visible') leave();
  };
  const scroll = (event: Event) => {
    const target = event.target as Node | null;
    if (target === document || target?.contains?.(element)) leave();
  };

  document.addEventListener('pointermove', move, { capture: true });
  document.addEventListener('pointerout', out, { capture: true });
  document.addEventListener('pointerup', up, { capture: true });
  document.addEventListener('pointercancel', leave, { capture: true });
  document.addEventListener('visibilitychange', visibility);
  document.addEventListener('scroll', scroll, { capture: true });
  element.addEventListener('pointerleave', leave);
  element.addEventListener('focusout', blur);
  view.addEventListener('blur', leave);
  view.addEventListener('resize', leave);

  return () => {
    cancelFrame();
    document.removeEventListener('pointermove', move, { capture: true });
    document.removeEventListener('pointerout', out, { capture: true });
    document.removeEventListener('pointerup', up, { capture: true });
    document.removeEventListener('pointercancel', leave, { capture: true });
    document.removeEventListener('visibilitychange', visibility);
    document.removeEventListener('scroll', scroll, { capture: true });
    element.removeEventListener('pointerleave', leave);
    element.removeEventListener('focusout', blur);
    view.removeEventListener('blur', leave);
    view.removeEventListener('resize', leave);
  };
}
